import { useState, useEffect, useMemo } from 'react'
import { supabase } from '../../supabaseClient'
import { C, st, formatDate } from '../../theme'
import { PageHeader, EmptyState, Modal, ScrollTable, Pagination, PAGE_SIZE } from '../../components/UI'
import { useAppPrefs } from '../../AppPrefs'
import { makeT } from '../../i18n'

const STATUS_INFO = {
  normal:  { label: '正常',  en: 'Normal',      color: C.green,   bg: C.greenBg,   border: C.greenBorder },
  late:    { label: '遲到',  en: 'Late',         color: C.yellow,  bg: C.yellowBg,  border: C.yellowBorder },
  early:   { label: '早退',  en: 'Early Leave',  color: '#f97316', bg: '#fff7ed',   border: '#fed7aa' },
  absent:  { label: '缺勤',  en: 'Absent',       color: C.red,     bg: C.redBg,     border: C.redBorder },
  leave:   { label: '請假',  en: 'Leave',        color: '#7c3aed', bg: '#ede9fe',   border: '#c4b5fd' },
}
const LEAVE_TYPES = [
  { value: 'annual',   label: '特休假' },
  { value: 'sick',     label: '病假' },
  { value: 'personal', label: '事假' },
  { value: 'official', label: '公假' },
  { value: 'other',    label: '其他' },
]
const LEAVE_STATUS = {
  pending:  { label: '待審核', color: C.yellow },
  approved: { label: '已核准', color: C.green },
  rejected: { label: '已拒絕', color: C.red },
}

const formatTime = iso => {
  if (!iso) return '--:--'
  const d = new Date(iso)
  return `${String(d.getHours()).padStart(2,'0')}:${String(d.getMinutes()).padStart(2,'0')}`
}
const calcDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371000, dLat = (lat2-lat1)*Math.PI/180, dLon = (lon2-lon1)*Math.PI/180
  const a = Math.sin(dLat/2)**2 + Math.cos(lat1*Math.PI/180)*Math.cos(lat2*Math.PI/180)*Math.sin(dLon/2)**2
  return Math.round(R*2*Math.atan2(Math.sqrt(a), Math.sqrt(1-a)))
}

// ── Isolated clock widget ──
function ClockDisplay({ today, settings, saving, onClockIn, onClockOut }) {
  const { lang } = useAppPrefs()
  const _ = makeT(lang)
  const [now, setNow] = useState(new Date())
  useEffect(() => { const t = setInterval(() => setNow(new Date()), 1000); return () => clearInterval(t) }, [])
  const clockStr = `${String(now.getHours()).padStart(2,'0')}:${String(now.getMinutes()).padStart(2,'0')}:${String(now.getSeconds()).padStart(2,'0')}`
  const dateStr = now.toLocaleDateString('zh-TW', { year:'numeric', month:'long', day:'numeric', weekday:'long' })
  const si = today ? STATUS_INFO[today.status] || STATUS_INFO.normal : null

  return (
    <div style={{ background: 'linear-gradient(135deg,#1e3a5f 0%,#2563eb 100%)', borderRadius: 20, padding: '36px 24px', marginBottom: 20, textAlign: 'center' }}>
      <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.7)', marginBottom: 8 }}>{dateStr}</div>
      <div style={{ fontSize: 54, fontWeight: 900, color: '#fff', fontFamily: 'monospace', letterSpacing: 4, marginBottom: 16 }}>{clockStr}</div>
      {today ? (
        <div style={{ display: 'flex', justifyContent: 'center', gap: 28, marginBottom: 20, flexWrap: 'wrap' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', marginBottom: 4 }}>{_('attClockInTime')}</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: '#86efac' }}>{formatTime(today.clock_in)}</div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', marginBottom: 4 }}>{_('attClockOutTime')}</div>
            <div style={{ fontSize: 24, fontWeight: 800, color: today.clock_out ? '#fde68a' : 'rgba(255,255,255,0.3)' }}>{formatTime(today.clock_out)}</div>
          </div>
          {today.work_hours > 0 && (
            <div style={{ textAlign: 'center' }}>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', marginBottom: 4 }}>{_('attWorkHours')}</div>
              <div style={{ fontSize: 24, fontWeight: 800, color: '#c4b5fd' }}>{today.work_hours}H</div>
            </div>
          )}
        </div>
      ) : (
        <div style={{ fontSize: 14, color: 'rgba(255,255,255,0.6)', marginBottom: 20 }}>{_('attNotClockedIn')} · Not clocked in yet</div>
      )}
      <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
        {!today?.clock_in ? (
          <button style={{ padding: '16px 40px', background: '#22c55e', border: 'none', borderRadius: 14, color: '#fff', fontSize: 18, fontWeight: 800, cursor: saving ? 'not-allowed' : 'pointer', boxShadow: '0 4px 20px rgba(34,197,94,0.4)', minWidth: 160, opacity: saving ? 0.7 : 1 }} onClick={onClockIn} disabled={saving}>
            <div>{saving ? '處理中...' : `▶ ${_('attClockInBtn')}`}</div>
            <div style={{ fontSize: 11, opacity: 0.8, marginTop: 2 }}>Clock In</div>
          </button>
        ) : !today?.clock_out ? (
          <button style={{ padding: '16px 40px', background: '#f59e0b', border: 'none', borderRadius: 14, color: '#fff', fontSize: 18, fontWeight: 800, cursor: saving ? 'not-allowed' : 'pointer', boxShadow: '0 4px 20px rgba(245,158,11,0.4)', minWidth: 160, opacity: saving ? 0.7 : 1 }} onClick={onClockOut} disabled={saving}>
            <div>{saving ? '處理中...' : `⏹ ${_('attClockOutBtn')}`}</div>
            <div style={{ fontSize: 11, opacity: 0.8, marginTop: 2 }}>Clock Out</div>
          </button>
        ) : (
          <div style={{ padding: '16px 40px', background: 'rgba(255,255,255,0.15)', borderRadius: 14, color: '#fff', fontSize: 16, fontWeight: 700, minWidth: 160 }}>
            <div>✅ 今日完成</div>
            <div style={{ fontSize: 11, opacity: 0.7, marginTop: 2 }}>Work Done Today</div>
          </div>
        )}
      </div>
      {today && si && (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
          <span style={{ padding: '6px 16px', borderRadius: 20, fontSize: 13, fontWeight: 700, background: 'rgba(255,255,255,0.15)', color: '#fff' }}>{si.label}</span>
          {today.location_verified && <span style={{ padding: '6px 16px', borderRadius: 20, fontSize: 13, fontWeight: 700, background: 'rgba(34,197,94,0.25)', color: '#86efac' }}>📍 GPS {today.distance_meters}m</span>}
          {today.clock_in && !today.location_verified && settings.require_location && <span style={{ padding: '6px 16px', borderRadius: 20, fontSize: 13, fontWeight: 700, background: 'rgba(239,68,68,0.25)', color: '#fca5a5' }}>⚠️ 位置未驗證</span>}
          {today.work_hours > 8 && <span style={{ padding: '6px 16px', borderRadius: 20, fontSize: 13, fontWeight: 700, background: 'rgba(196,181,253,0.3)', color: '#c4b5fd' }}>⏱ 加班 {Math.round((today.work_hours-8)*10)/10}H</span>}
        </div>
      )}
    </div>
  )
}

// ── Mini bar chart ──
function ColBarChart({ data, color = C.accent }) {
  const max = Math.max(...data.map(d => d.v), 1)
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 80, padding: '0 2px' }}>
      {data.map((d, i) => (
        <div key={i} style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <div style={{ fontSize: 9, color: C.muted }}>{d.v > 0 ? d.v : ''}</div>
          <div style={{ width: '100%', height: Math.max(3, (d.v/max)*58), background: `${color}cc`, borderRadius: '3px 3px 0 0', transition: 'height 0.4s' }} />
          <div style={{ fontSize: 9, color: C.muted, textAlign: 'center', maxWidth: 28, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{d.k}</div>
        </div>
      ))}
    </div>
  )
}

export default function Attendance({ userProfile, showToast, addLog, isMobile = false }) {
  const isAdmin = userProfile?.role === 'admin'
  const pos = userProfile?.position || 'user'
  const canManage = isAdmin || ['factory_manager','senior_manager','section_chief','team_leader'].includes(pos)

  const { lang } = useAppPrefs()
  const _ = makeT(lang)

  const STATUS_INFO = {
    normal:  { label: _('attStatusNormal'),     en: 'Normal',      color: C.green,   bg: C.greenBg,   border: C.greenBorder },
    late:    { label: _('attStatusLate'),       en: 'Late',         color: C.yellow,  bg: C.yellowBg,  border: C.yellowBorder },
    early:   { label: _('attStatusEarlyLeave'), en: 'Early Leave',  color: '#f97316', bg: '#fff7ed',   border: '#fed7aa' },
    absent:  { label: _('attStatusAbsent'),     en: 'Absent',       color: C.red,     bg: C.redBg,     border: C.redBorder },
    leave:   { label: '請假',                    en: 'Leave',        color: '#7c3aed', bg: '#ede9fe',   border: '#c4b5fd' },
  }
  const LEAVE_TYPES = [
    { value: 'annual',   label: _('attLeaveAnnual') },
    { value: 'sick',     label: _('attLeaveSick') },
    { value: 'personal', label: _('attLeavePersonal') },
    { value: 'official', label: _('attLeaveOfficial') },
    { value: 'other',    label: _('attLeaveOther') },
  ]

  const [activeTab, setActiveTab] = useState('my')
  const [records, setRecords] = useState([])
  const [users, setUsers] = useState([])
  const [leaveRequests, setLeaveRequests] = useState([])
  const [settings, setSettings] = useState({ work_start:'08:00', work_end:'17:00', late_threshold:10, latitude:null, longitude:null, allowed_radius:100, require_location:false })
  const [saving, setSaving] = useState(false)
  const [today, setToday] = useState(null)

  // Admin: records filter
  const [filterUser, setFilterUser] = useState('all')
  const [filterDateFrom, setFilterDateFrom] = useState('')
  const [filterDateTo, setFilterDateTo] = useState('')
  const [filterStatus, setFilterStatus] = useState('all')
  const [page, setPage] = useState(1)

  // Admin: manual add
  const [showManualAdd, setShowManualAdd] = useState(false)
  const [manualForm, setManualForm] = useState({ user_id:'', date:'', clock_in:'', clock_out:'', status:'normal', note:'' })

  // Admin: settings
  const [showSettings, setShowSettings] = useState(false)

  // Calendar
  const [calMonth, setCalMonth] = useState(() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}` })
  const [calUser, setCalUser] = useState('self')

  // Leave request
  const [leaveForm, setLeaveForm] = useState({ leave_type:'annual', start_date:'', end_date:'', reason:'' })
  const [submittingLeave, setSubmittingLeave] = useState(false)
  const [leaveTab, setLeaveTab] = useState('mine') // 'mine' | 'all' (admin)
  const [leaveTableReady, setLeaveTableReady] = useState(true) // false = table missing

  // Edit record modal
  const [editRecord, setEditRecord] = useState(null)
  const [editForm, setEditForm] = useState({})

  // Edit leave modal
  const [editLeave, setEditLeave] = useState(null)
  const [editLeaveForm, setEditLeaveForm] = useState({})

  // Reject with note modal
  const [rejectModal, setRejectModal] = useState(null) // { id, userName }
  const [rejectNote, setRejectNote] = useState('')

  const todayStr = new Date().toISOString().slice(0,10)

  const load = async () => {
    const cid = userProfile?.company_id
    let attQ = supabase.from('attendance').select('*').order('date',{ascending:false}).order('clock_in',{ascending:false}).limit(2000)
    if (cid) attQ = attQ.eq('company_id', cid)
    let usrQ = supabase.from('user_profiles').select('id,name,employee_id,position,department').eq('approved',true).order('name')
    if (cid) usrQ = usrQ.eq('company_id', cid)
    let setQ = supabase.from('attendance_settings').select('*').limit(1)
    if (cid) setQ = setQ.eq('company_id', cid)
    const [{ data: recs }, { data: usrs }, { data: setts }] = await Promise.all([attQ, usrQ, setQ])
    if (recs) setRecords(recs)
    if (usrs) setUsers(usrs)
    if (setts?.length > 0) setSettings(setts[0])
    if (recs && userProfile?.id) {
      setToday(recs.find(r => r.user_id === userProfile.id && r.date === todayStr) || null)
    }
    // leave_requests is a newer table — check separately so it doesn't block main load
    let leaveQ = supabase.from('leave_requests').select('*').order('created_at',{ascending:false}).limit(500)
    if (cid) leaveQ = leaveQ.eq('company_id', cid)
    const { data: leaves, error: leaveErr } = await leaveQ
    if (leaveErr) {
      // Table probably doesn't exist yet
      setLeaveTableReady(false)
    } else {
      setLeaveTableReady(true)
      if (leaves) setLeaveRequests(leaves)
    }
  }
  useEffect(() => { load() }, [userProfile?.id])

  // ── GPS ──
  const getLocation = () => new Promise((resolve, reject) => {
    if (!navigator.geolocation) { reject(new Error('此裝置不支援 GPS 定位')); return }
    navigator.geolocation.getCurrentPosition(resolve, reject, { timeout:10000, enableHighAccuracy:true })
  })

  // ── Clock In ──
  const handleClockIn = async () => {
    if (today?.clock_in) { showToast('今天已打過上班卡！', 'error'); return }
    setSaving(true)
    let latitude=null, longitude=null, location_verified=false, distance_meters=null
    if (settings.require_location && settings.latitude && settings.longitude) {
      try {
        showToast('📍 取得位置中...','info')
        const p = await getLocation()
        latitude = p.coords.latitude; longitude = p.coords.longitude
        distance_meters = calcDistance(latitude, longitude, settings.latitude, settings.longitude)
        if (distance_meters > (settings.allowed_radius||100)) {
          showToast(`❌ 打卡失敗！距離工廠 ${distance_meters}m，超出 ${settings.allowed_radius}m 範圍`, 'error')
          setSaving(false); return
        }
        location_verified = true
      } catch { showToast('❌ 無法取得定位，請確認已開啟位置權限', 'error'); setSaving(false); return }
    }
    const now = new Date()
    const [wh, wm] = (settings.work_start||'08:00').split(':').map(Number)
    const threshold = settings.late_threshold || 10
    const isLate = now.getHours() > wh || (now.getHours() === wh && now.getMinutes() > wm + threshold)
    const { data, error } = await supabase.from('attendance').insert({
      user_id: userProfile.id, user_name: userProfile.name,
      employee_id: userProfile.employee_id||'', date: todayStr,
      clock_in: now.toISOString(), status: isLate ? 'late' : 'normal',
      latitude, longitude, location_verified, distance_meters,
      company_id: userProfile?.company_id || null,
    }).select().single()
    if (error) { showToast('打卡失敗：'+error.message,'error'); setSaving(false); return }
    if (data) setToday(data)
    try { await addLog('打卡', `${userProfile.name} 上班打卡${isLate?'（遲到）':''}`) } catch(_) {}
    showToast(isLate ? '⚠️ 上班打卡成功，但已遲到' : '✓ 上班打卡成功！')
    setSaving(false); load()
  }

  // ── Clock Out ──
  const handleClockOut = async () => {
    if (!today?.clock_in) { showToast('尚未打上班卡', 'error'); return }
    if (today?.clock_out) { showToast('今天已打過下班卡！', 'error'); return }
    setSaving(true)
    const now = new Date()
    const workHours = Math.round((now - new Date(today.clock_in)) / 36000) / 100
    const [wh, wm] = (settings.work_end||'17:00').split(':').map(Number)
    const isEarly = now.getHours() < wh || (now.getHours() === wh && now.getMinutes() < wm)
    const newStatus = isEarly ? 'early' : today.status === 'late' ? 'late' : 'normal'
    const { error } = await supabase.from('attendance').update({ clock_out: now.toISOString(), work_hours: workHours, status: newStatus }).eq('id', today.id)
    if (error) { showToast('打卡失敗：'+error.message,'error'); setSaving(false); return }
    try { await addLog('打卡', `${userProfile.name} 下班打卡，工時 ${workHours}H`) } catch(_) {}
    showToast(`✓ 下班打卡成功！工時 ${workHours} 小時${workHours > 8 ? ` （加班 ${Math.round((workHours-8)*10)/10}H）` : ''}`)
    setSaving(false); load()
  }

  // ── Manual Add (Admin) ──
  const handleManualAdd = async () => {
    if (!manualForm.user_id || !manualForm.date || !manualForm.clock_in) { showToast('請填寫員工、日期、上班時間','error'); return }
    const user = users.find(u => u.id === manualForm.user_id)
    if (!user) return
    // Check duplicate
    const { data: existing } = await supabase.from('attendance').select('id').eq('user_id', manualForm.user_id).eq('date', manualForm.date).maybeSingle()
    if (existing) { showToast(`${user.name} 在 ${manualForm.date} 已有打卡紀錄，請先刪除原紀錄`, 'error'); return }
    setSaving(true)
    const clockIn = new Date(`${manualForm.date}T${manualForm.clock_in}:00`)
    const clockOut = manualForm.clock_out ? new Date(`${manualForm.date}T${manualForm.clock_out}:00`) : null
    const workHours = clockOut ? Math.round((clockOut - clockIn) / 36000) / 100 : 0
    const { error } = await supabase.from('attendance').insert({
      user_id: manualForm.user_id, user_name: user.name,
      employee_id: user.employee_id||'', date: manualForm.date,
      clock_in: clockIn.toISOString(),
      clock_out: clockOut?.toISOString() || null,
      work_hours: workHours, status: manualForm.status,
      note: manualForm.note || '管理員手動新增',
      company_id: userProfile?.company_id || null,
    })
    setSaving(false)
    if (error) { showToast('新增失敗：'+error.message,'error'); return }
    try { await addLog('手動補打卡', `${user.name} ${manualForm.date}`) } catch(_) {}
    showToast('✓ 已補打卡'); setShowManualAdd(false)
    setManualForm({ user_id:'', date:'', clock_in:'', clock_out:'', status:'normal', note:'' })
    load()
  }

  // ── Edit Record ──
  const handleEditRecord = async () => {
    if (!editRecord) return
    setSaving(true)
    const clockIn = editForm.clock_in ? new Date(`${editRecord.date}T${editForm.clock_in}:00`) : null
    const clockOut = editForm.clock_out ? new Date(`${editRecord.date}T${editForm.clock_out}:00`) : null
    const workHours = clockIn && clockOut ? Math.round((clockOut - clockIn) / 36000) / 100 : editRecord.work_hours
    const { error } = await supabase.from('attendance').update({
      clock_in: clockIn?.toISOString() || editRecord.clock_in,
      clock_out: clockOut?.toISOString() || null,
      work_hours: workHours, status: editForm.status,
      note: editForm.note,
    }).eq('id', editRecord.id)
    setSaving(false)
    if (error) { showToast('更新失敗：'+error.message,'error'); return }
    showToast('✓ 已更新'); setEditRecord(null); load()
  }

  // ── Delete Record ──
  const handleDeleteRecord = async (id) => {
    if (!window.confirm('確定刪除此打卡紀錄？此操作無法復原。')) return
    const { data: deleted, error } = await supabase.from('attendance').delete().eq('id', id).select('id')
    if (error) { showToast('刪除失敗：'+error.message,'error'); return }
    if (!deleted || deleted.length === 0) {
      showToast('刪除失敗：權限不足，請在 Supabase SQL Editor 執行：\nCREATE POLICY "已登入用戶可刪除出勤" ON attendance FOR DELETE TO authenticated USING (true);','error')
      return
    }
    showToast('✓ 已刪除'); load()
  }

  // ── Delete Leave ──
  const handleDeleteLeave = async (id, userName) => {
    if (!window.confirm(`確定刪除 ${userName} 的請假申請？此操作無法復原。`)) return
    const { data: deleted, error } = await supabase.from('leave_requests').delete().eq('id', id).select('id')
    if (error) { showToast('刪除失敗：'+error.message,'error'); return }
    if (!deleted || deleted.length === 0) {
      showToast('刪除失敗：權限不足，請在 Supabase SQL Editor 執行：\nCREATE POLICY "已登入用戶可刪除請假" ON leave_requests FOR DELETE TO authenticated USING (true);','error')
      return
    }
    try { await addLog('刪除請假', `管理員刪除 ${userName} 的請假申請`) } catch(_) {}
    showToast('✓ 已刪除'); load()
  }

  // ── Edit Leave ──
  const handleEditLeave = async () => {
    if (!editLeave) return
    if (!editLeaveForm.start_date || !editLeaveForm.end_date) { showToast('請填寫日期','error'); return }
    if (editLeaveForm.end_date < editLeaveForm.start_date) { showToast('結束日期不能早於開始日期','error'); return }
    setSaving(true)
    const days = Math.round((new Date(editLeaveForm.end_date) - new Date(editLeaveForm.start_date)) / 86400000) + 1
    const { error } = await supabase.from('leave_requests').update({
      leave_type: editLeaveForm.leave_type,
      start_date: editLeaveForm.start_date,
      end_date: editLeaveForm.end_date,
      days,
      reason: editLeaveForm.reason,
      status: editLeaveForm.status,
    }).eq('id', editLeave.id)
    setSaving(false)
    if (error) { showToast('更新失敗：'+error.message,'error'); return }
    try { await addLog('修改請假', `管理員修改 ${editLeave.user_name} 的請假申請`) } catch(_) {}
    showToast('✓ 已更新'); setEditLeave(null); load()
  }

  // ── Leave Request ──
  const handleSubmitLeave = async () => {
    if (!leaveTableReady) { showToast('請假功能尚未啟用，請聯絡管理員執行資料庫設定', 'error'); return }
    if (!leaveForm.start_date || !leaveForm.end_date || !leaveForm.reason.trim()) {
      showToast('請填寫日期和請假原因', 'error'); return
    }
    if (leaveForm.end_date < leaveForm.start_date) { showToast('結束日期不能早於開始日期','error'); return }
    setSubmittingLeave(true)
    const start = new Date(leaveForm.start_date), end = new Date(leaveForm.end_date)
    const days = Math.round((end - start) / 86400000) + 1
    const { error } = await supabase.from('leave_requests').insert({
      user_id: userProfile.id, user_name: userProfile.name,
      leave_type: leaveForm.leave_type, start_date: leaveForm.start_date,
      end_date: leaveForm.end_date, days, reason: leaveForm.reason, status: 'pending',
      company_id: userProfile?.company_id || null,
    })
    setSubmittingLeave(false)
    if (error) { showToast('申請失敗：'+error.message,'error'); return }
    try { await addLog('請假申請', `${userProfile.name} 申請 ${leaveForm.start_date}~${leaveForm.end_date}`) } catch(_) {}
    showToast('✓ 請假申請已送出，等待審核')
    setLeaveForm({ leave_type:'annual', start_date:'', end_date:'', reason:'' })
    load()
  }

  const handleReviewLeave = async (id, status, note = '') => {
    const { error } = await supabase.from('leave_requests').update({
      status, reviewer_id: userProfile.id, reviewer_name: userProfile.name,
      reviewer_note: note, reviewed_at: new Date().toISOString(),
    }).eq('id', id)
    if (error) { showToast('操作失敗：'+error.message,'error'); return }
    showToast(status === 'approved' ? '✓ 已核准' : '✓ 已拒絕')
    load()
  }

  // ── Settings ──
  const handleSaveSettings = async () => {
    setSaving(true)
    const { data: ex } = await supabase.from('attendance_settings').select('id').limit(1)
    if (ex?.length > 0) await supabase.from('attendance_settings').update(settings).eq('id', ex[0].id)
    else await supabase.from('attendance_settings').insert({ ...settings, company_id: userProfile?.company_id || null })
    showToast('✓ 設定已儲存'); setShowSettings(false); setSaving(false); load()
  }

  // ── Export CSV ──
  const handleExportCSV = () => {
    const data = filteredRecords
    const headers = ['日期','員工','工號','上班時間','下班時間','工時','狀態','GPS驗證','距離(m)','備注']
    const rows = data.map(r => [
      r.date, r.user_name, r.employee_id||'', formatTime(r.clock_in), formatTime(r.clock_out),
      r.work_hours||0, STATUS_INFO[r.status]?.label||r.status,
      r.location_verified?'✓':'', r.distance_meters||'', r.note||''
    ])
    const csv = [headers,...rows].map(r=>r.map(v=>`"${v}"`).join(',')).join('\n')
    const blob = new Blob(['\uFEFF'+csv],{type:'text/csv;charset=utf-8;'})
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a'); a.href=url
    a.download = `YPFOPS_出勤紀錄_${new Date().toISOString().slice(0,10)}.csv`; a.click()
    URL.revokeObjectURL(url)
  }

  // ── Derived Data ──
  const filteredRecords = useMemo(() => records.filter(r => {
    if (filterUser !== 'all' && r.user_id !== filterUser) return false
    if (filterStatus !== 'all' && r.status !== filterStatus) return false
    if (filterDateFrom && r.date < filterDateFrom) return false
    if (filterDateTo && r.date > filterDateTo) return false
    return true
  }), [records, filterUser, filterStatus, filterDateFrom, filterDateTo])

  const paged = useMemo(() => filteredRecords.slice((page-1)*PAGE_SIZE, page*PAGE_SIZE), [filteredRecords, page])
  const totalPages = useMemo(() => Math.ceil(filteredRecords.length / PAGE_SIZE), [filteredRecords])

  const monthStr = new Date().toISOString().slice(0,7)

  const stats = useMemo(() => {
    const todayRecs = records.filter(r => r.date === todayStr)
    const monthRecs = records.filter(r => r.date?.startsWith(monthStr))
    const myMonthRecs = records.filter(r => r.user_id === userProfile?.id && r.date?.startsWith(monthStr))
    const myHours = myMonthRecs.reduce((a,r) => a+(r.work_hours||0), 0)
    const myRecent = records.filter(r => r.user_id === userProfile?.id).slice(0, 14)
    const notClockedIn = users.filter(u => !todayRecs.find(r => r.user_id === u.id))
    return { todayRecs, monthRecs, myMonthRecs, myHours, myRecent, notClockedIn }
  }, [records, users, userProfile?.id, todayStr, monthStr])

  // ── Calendar Data ──
  const calData = useMemo(() => {
    const [y, m] = calMonth.split('-').map(Number)
    const firstDay = new Date(y, m-1, 1).getDay() // 0=Sun
    const daysInMonth = new Date(y, m, 0).getDate()
    const targetUserId = calUser === 'self' ? userProfile?.id : calUser
    const userRecs = records.filter(r => r.user_id === targetUserId && r.date?.startsWith(calMonth))
    const recByDate = {}
    userRecs.forEach(r => { recByDate[r.date] = r })
    // Leave requests in this month
    const userLeaves = leaveRequests.filter(l => l.user_id === targetUserId && l.status === 'approved'
      && ((l.start_date <= `${calMonth}-31`) && (l.end_date >= `${calMonth}-01`)))
    return { y, m, firstDay, daysInMonth, recByDate, userLeaves }
  }, [calMonth, calUser, records, leaveRequests, userProfile?.id])

  // ── Analytics ──
  const analyticsData = useMemo(() => {
    // Per-person stats (this month)
    const monthRecs = records.filter(r => r.date?.startsWith(monthStr))
    const perPerson = {}
    users.forEach(u => { perPerson[u.id] = { name: u.name, days: 0, hours: 0, late: 0, early: 0, absent: 0, overtime: 0 } })
    monthRecs.forEach(r => {
      if (!perPerson[r.user_id]) perPerson[r.user_id] = { name: r.user_name, days: 0, hours: 0, late: 0, early: 0, absent: 0, overtime: 0 }
      const p = perPerson[r.user_id]
      p.days++; p.hours += r.work_hours||0
      if (r.status === 'late') p.late++
      if (r.status === 'early') p.early++
      if (r.status === 'absent') p.absent++
      if ((r.work_hours||0) > 8) p.overtime += Math.round(((r.work_hours||0)-8)*10)/10
    })
    // Monthly trend (last 6 months)
    const months = []
    for (let i=5; i>=0; i--) {
      const d = new Date(); d.setMonth(d.getMonth()-i)
      const key = `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`
      months.push({ k: key.slice(5), v: records.filter(r => r.date?.startsWith(key)).length })
    }
    const sortedByHours = Object.values(perPerson).filter(p => p.days > 0).sort((a,b) => b.hours - a.hours)
    const sortedByLate = Object.values(perPerson).filter(p => p.late > 0).sort((a,b) => b.late - a.late)
    return { perPerson: Object.values(perPerson), months, sortedByHours, sortedByLate }
  }, [records, users, monthStr])

  // ── Tabs ──
  const pendingLeaveCount = canManage ? leaveRequests.filter(l => l.status === 'pending').length : 0
  const tabs = [
    { key: 'my',       label: `⏰ ${_('attTabMyClock')}`,   en: 'My Clock' },
    { key: 'calendar', label: `📅 ${_('attTabCalendar')}`,  en: 'Calendar' },
    { key: 'leave',    label: `📋 ${_('attTabLeave')}`,     en: 'Leave', badge: pendingLeaveCount || null },
    ...(canManage ? [
      { key: 'admin',    label: `👥 ${_('attTabManage')}`,  en: 'Manage', badge: pendingLeaveCount || null },
      { key: 'analytics',label: `📊 ${_('attTabAnalytics')}`, en: 'Analytics' },
    ] : []),
  ]

  return (
    <div>
      <PageHeader title="出勤管理" en="Attendance Management" vi="Chấm công" />

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        {tabs.map(t => (
          <button key={t.key} style={{ ...st.btn(activeTab===t.key?'primary':'default'), padding:'10px 16px', position:'relative' }} onClick={() => setActiveTab(t.key)}>
            <span style={{ display:'flex', alignItems:'center', gap:6 }}>
              {t.label}
              {t.badge > 0 && <span style={{ display:'inline-block', padding:'1px 7px', background:'#ef4444', color:'#fff', borderRadius:10, fontSize:10, fontWeight:900, lineHeight:1.5, verticalAlign:'middle' }}>{t.badge}</span>}
            </span>
            <span style={{ fontSize:9, opacity:0.45, display:'block' }}>{t.en}</span>
          </button>
        ))}
      </div>

      {/* ───────── 我的打卡 ───────── */}
      {activeTab === 'my' && (
        <>
          <ClockDisplay today={today} settings={settings} saving={saving} onClockIn={handleClockIn} onClockOut={handleClockOut} />

          {/* My stats */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:12, marginBottom:20 }}>
            {[
              { icon:'📅', label:_('attMonthlyAttendance'), value:`${stats.myMonthRecs.length} 天`, color:C.accent, bg:C.accentDim },
              { icon:'⏱️', label:_('attMonthlyHours'), value:`${Math.round(stats.myHours*10)/10} H`, color:'#7c3aed', bg:'#ede9fe' },
              { icon:'⚠️', label:_('attMonthlyLate'), value:`${stats.myMonthRecs.filter(r=>r.status==='late').length} 次`, color:C.yellow, bg:C.yellowBg },
              { icon:'🌙', label:_('attMonthlyOvertime'), value:`${Math.round(stats.myMonthRecs.reduce((a,r)=>a+Math.max(0,(r.work_hours||0)-8),0)*10)/10} H`, color:'#7c3aed', bg:'#ede9fe' },
            ].map((k,i) => (
              <div key={i} style={{ background:k.bg, border:`2px solid ${k.color}33`, borderRadius:14, padding:'14px 16px' }}>
                <div style={{ fontSize:22, marginBottom:4 }}>{k.icon}</div>
                <div style={{ fontSize:24, fontWeight:900, color:k.color }}>{k.value}</div>
                <div style={{ fontSize:12, color:C.sub, fontWeight:600, marginTop:3 }}>{k.label}</div>
              </div>
            ))}
          </div>

          {/* Pending leave requests for me */}
          {leaveRequests.filter(l => l.user_id === userProfile?.id && l.status === 'pending').length > 0 && (
            <div style={{ background:C.yellowBg, border:`1.5px solid ${C.yellowBorder}`, borderRadius:12, padding:'12px 16px', marginBottom:16 }}>
              <div style={{ fontSize:13, fontWeight:700, color:C.yellow }}>⏳ 您有 {leaveRequests.filter(l=>l.user_id===userProfile?.id&&l.status==='pending').length} 筆請假申請待審核</div>
            </div>
          )}

          {/* Recent records */}
          <div style={st.card}>
            <div style={{ fontSize:15, fontWeight:700, color:C.text, marginBottom:14 }}>📋 最近出勤紀錄（14 天）</div>
            {stats.myRecent.length === 0 ? <EmptyState icon="⏰" text={_('attNoClockRecords')} /> : stats.myRecent.map(r => {
              const si = STATUS_INFO[r.status] || STATUS_INFO.normal
              const ot = (r.work_hours||0) > 8 ? Math.round(((r.work_hours||0)-8)*10)/10 : 0
              return (
                <div key={r.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'12px 0', borderBottom:`1px solid ${C.border}`, flexWrap:'wrap', gap:8 }}>
                  <div>
                    <div style={{ fontSize:15, fontWeight:700, color:C.text }}>{r.date}</div>
                    <div style={{ fontSize:12, color:C.muted, marginTop:2 }}>
                      上班 {formatTime(r.clock_in)} · 下班 {formatTime(r.clock_out)}
                      {r.work_hours > 0 && ` · ${r.work_hours}H`}
                      {ot > 0 && <span style={{ color:'#7c3aed', fontWeight:700 }}> (+{ot}H 加班)</span>}
                    </div>
                    {r.note && <div style={{ fontSize:11, color:C.muted, marginTop:2 }}>備注：{r.note}</div>}
                  </div>
                  <span style={{ padding:'4px 12px', borderRadius:12, fontSize:12, fontWeight:700, background:si.bg, color:si.color, border:`1.5px solid ${si.border}` }}>{si.label}</span>
                </div>
              )
            })}
          </div>
        </>
      )}

      {/* ───────── 月曆 ───────── */}
      {activeTab === 'calendar' && (
        <>
          {/* Controls */}
          <div style={{ display:'flex', gap:8, marginBottom:16, flexWrap:'wrap', alignItems:'center' }}>
            <button style={{ ...st.btn('default'), padding:'10px 14px' }} onClick={() => {
              const [y,m] = calMonth.split('-').map(Number)
              const d = new Date(y, m-2, 1)
              setCalMonth(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`)
            }}>‹ {_('attCalPrevMonth')}</button>
            <div style={{ fontSize:16, fontWeight:800, color:C.text, padding:'10px 16px' }}>
              {calMonth.replace('-','年')}月
            </div>
            <button style={{ ...st.btn('default'), padding:'10px 14px' }} onClick={() => {
              const [y,m] = calMonth.split('-').map(Number)
              const d = new Date(y, m, 1)
              setCalMonth(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`)
            }}>{_('attCalNextMonth')} ›</button>
            {canManage && (
              <select style={{ ...st.input, width:140 }} value={calUser} onChange={e => setCalUser(e.target.value)}>
                <option value="self">我自己</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
            )}
            <button style={{ ...st.btn('default'), padding:'10px 14px', marginLeft:'auto' }} onClick={() => {
              const d = new Date(); setCalMonth(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`)
            }}>{_('attReturnThisMonth')}</button>
          </div>

          {/* Legend */}
          <div style={{ display:'flex', gap:10, marginBottom:14, flexWrap:'wrap' }}>
            {Object.entries(STATUS_INFO).map(([k,v]) => (
              <div key={k} style={{ display:'flex', alignItems:'center', gap:4, fontSize:12 }}>
                <div style={{ width:12, height:12, borderRadius:3, background:v.bg, border:`1.5px solid ${v.color}` }} />
                <span style={{ color:C.muted }}>{v.label}</span>
              </div>
            ))}
            <div style={{ display:'flex', alignItems:'center', gap:4, fontSize:12 }}>
              <div style={{ width:12, height:12, borderRadius:3, background:'#f0f0f0', border:`1.5px solid #ccc` }} />
              <span style={{ color:C.muted }}>假日</span>
            </div>
          </div>

          {/* Calendar grid */}
          <div style={st.card}>
            {/* Day headers */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:2, marginBottom:4 }}>
              {['日','一','二','三','四','五','六'].map((d,i) => (
                <div key={d} style={{ textAlign:'center', fontSize:12, fontWeight:700, color: i===0||i===6 ? C.red : C.muted, padding:'6px 0' }}>{d}</div>
              ))}
            </div>
            {/* Calendar cells */}
            <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:3 }}>
              {Array.from({ length: calData.firstDay }).map((_,i) => <div key={`e${i}`} />)}
              {Array.from({ length: calData.daysInMonth }).map((_,i) => {
                const day = i+1
                const dateStr2 = `${calData.y}-${String(calData.m).padStart(2,'0')}-${String(day).padStart(2,'0')}`
                const rec = calData.recByDate[dateStr2]
                const isWeekend = [0,6].includes(new Date(dateStr2).getDay())
                const isToday = dateStr2 === todayStr
                const isFuture = dateStr2 > todayStr
                const hasLeave = calData.userLeaves.some(l => l.start_date <= dateStr2 && l.end_date >= dateStr2)
                const si = rec ? STATUS_INFO[rec.status] || STATUS_INFO.normal : hasLeave ? STATUS_INFO.leave : null
                return (
                  <div key={day} style={{
                    minHeight: 62, borderRadius: 10, padding: '6px 6px 4px',
                    background: si ? si.bg : isWeekend ? '#f8fafc' : '#fff',
                    border: `2px solid ${isToday ? C.accent : si ? si.border : C.border}`,
                    opacity: isFuture && !hasLeave ? 0.5 : 1,
                    transition: 'all 0.15s',
                  }}>
                    <div style={{ fontSize:12, fontWeight: isToday ? 900 : 700, color: isToday ? C.accent : isWeekend ? C.red : C.text, marginBottom:3 }}>{day}</div>
                    {rec && (
                      <>
                        <div style={{ fontSize:9, color: si.color, fontWeight:700, lineHeight:1.2 }}>{si.label}</div>
                        <div style={{ fontSize:9, color:C.muted, lineHeight:1.3 }}>
                          {formatTime(rec.clock_in)}{rec.clock_out ? `~${formatTime(rec.clock_out)}` : ''}
                        </div>
                        {rec.work_hours > 0 && <div style={{ fontSize:9, color: rec.work_hours>8?'#7c3aed':C.muted }}>{rec.work_hours}H{rec.work_hours>8?'⚡':''}</div>}
                      </>
                    )}
                    {hasLeave && !rec && <div style={{ fontSize:9, color:'#7c3aed', fontWeight:700 }}>請假</div>}
                    {!rec && !hasLeave && !isWeekend && !isFuture && (
                      <div style={{ fontSize:9, color:C.red, fontWeight:600 }}>缺勤</div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>

          {/* Monthly summary */}
          {(() => {
            const targetId = calUser === 'self' ? userProfile?.id : calUser
            const monthRecs = records.filter(r => r.user_id === targetId && r.date?.startsWith(calMonth))
            const totalH = monthRecs.reduce((a,r) => a+(r.work_hours||0),0)
            const lateN = monthRecs.filter(r => r.status==='late').length
            const otH = monthRecs.reduce((a,r) => a+Math.max(0,(r.work_hours||0)-8), 0)
            return monthRecs.length > 0 ? (
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(130px,1fr))', gap:10, marginTop:14 }}>
                {[
                  { label:'出勤天數', value:`${monthRecs.length}天`, color:C.accent },
                  { label:'總工時', value:`${Math.round(totalH*10)/10}H`, color:'#7c3aed' },
                  { label:'遲到次數', value:`${lateN}次`, color:C.yellow },
                  { label:'加班工時', value:`${Math.round(otH*10)/10}H`, color:C.green },
                ].map((k,i) => (
                  <div key={i} style={{ padding:'12px 14px', borderRadius:12, background:'#f8fafc', border:`1.5px solid ${C.border}`, textAlign:'center' }}>
                    <div style={{ fontSize:20, fontWeight:900, color:k.color }}>{k.value}</div>
                    <div style={{ fontSize:11, color:C.muted, marginTop:3, fontWeight:600 }}>{k.label}</div>
                  </div>
                ))}
              </div>
            ) : null
          })()}
        </>
      )}

      {/* ───────── 請假申請 ───────── */}
      {activeTab === 'leave' && (
        <>
          {/* Table not ready banner */}
          {!leaveTableReady && (
            <div style={{ background:'#fef2f2', border:'2px solid #fca5a5', borderRadius:14, padding:'16px 20px', marginBottom:16 }}>
              <div style={{ fontSize:15, fontWeight:800, color:C.red, marginBottom:10 }}>⚠️ 請假功能尚未啟用</div>
              <div style={{ fontSize:13, color:'#7f1d1d', marginBottom:12 }}>需要先在 Supabase SQL Editor 執行以下指令來建立 <code>leave_requests</code> 資料表：</div>
              <pre style={{ background:'#1e1e2e', color:'#cdd6f4', borderRadius:10, padding:'14px 16px', fontSize:12, overflowX:'auto', lineHeight:1.7, margin:0 }}>{`CREATE TABLE IF NOT EXISTS leave_requests (
  id UUID DEFAULT gen_random_uuid() PRIMARY KEY,
  user_id UUID REFERENCES user_profiles(id) ON DELETE CASCADE,
  user_name TEXT,
  leave_type TEXT DEFAULT 'personal',
  start_date DATE NOT NULL,
  end_date DATE NOT NULL,
  days NUMERIC(5,1),
  reason TEXT,
  status TEXT DEFAULT 'pending',
  reviewer_id UUID,
  reviewer_name TEXT,
  reviewer_note TEXT,
  reviewed_at TIMESTAMPTZ,
  created_at TIMESTAMPTZ DEFAULT NOW()
);
ALTER TABLE leave_requests ENABLE ROW LEVEL SECURITY;
CREATE POLICY "已登入用戶可讀取請假" ON leave_requests FOR SELECT TO authenticated USING (true);
CREATE POLICY "已登入用戶可新增請假" ON leave_requests FOR INSERT TO authenticated WITH CHECK (true);
CREATE POLICY "已登入用戶可更新請假" ON leave_requests FOR UPDATE TO authenticated USING (true);`}</pre>
              <div style={{ fontSize:12, color:C.muted, marginTop:10 }}>執行後重新整理頁面即可使用。</div>
            </div>
          )}

          {/* Submit form */}
          <div style={{ ...st.card, marginBottom:16, border:`2px solid ${!leaveTableReady ? C.border : C.accent}`, opacity: !leaveTableReady ? 0.6 : 1 }}>
            <div style={{ fontSize:15, fontWeight:800, color:C.text, marginBottom:14 }}>📋 新增請假申請</div>
            <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(170px,1fr))', gap:14 }}>
              <div>
                <label style={st.label}>{_('attLeaveType')}</label>
                <select style={st.input} value={leaveForm.leave_type} onChange={e => setLeaveForm(f=>({...f,leave_type:e.target.value}))}>
                  {LEAVE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div>
                <label style={st.label}>{_('attStartDate')}</label>
                <input type="date" style={st.input} value={leaveForm.start_date} onChange={e => setLeaveForm(f=>({...f,start_date:e.target.value}))} />
              </div>
              <div>
                <label style={st.label}>{_('attEndDate')}</label>
                <input type="date" style={st.input} value={leaveForm.end_date} min={leaveForm.start_date} onChange={e => setLeaveForm(f=>({...f,end_date:e.target.value}))} />
              </div>
              {leaveForm.start_date && leaveForm.end_date && leaveForm.end_date >= leaveForm.start_date && (
                <div style={{ display:'flex', alignItems:'center', justifyContent:'center', padding:'12px 16px', background:C.accentDim, borderRadius:10, border:`1.5px solid ${C.accent}33` }}>
                  <div style={{ textAlign:'center' }}>
                    <div style={{ fontSize:24, fontWeight:900, color:C.accent }}>{Math.round((new Date(leaveForm.end_date)-new Date(leaveForm.start_date))/86400000)+1}</div>
                    <div style={{ fontSize:11, color:C.muted }}>天</div>
                  </div>
                </div>
              )}
            </div>
            <div style={{ marginTop:14 }}>
              <label style={st.label}>{_('attLeaveReason')}</label>
              <textarea style={{ ...st.input, height:70, resize:'vertical' }} value={leaveForm.reason} placeholder="請填寫請假原因..." onChange={e => setLeaveForm(f=>({...f,reason:e.target.value}))} />
            </div>
            <button style={{ ...st.btn(), marginTop:14, padding:'12px 24px' }} onClick={handleSubmitLeave} disabled={submittingLeave || !leaveTableReady}>
              {submittingLeave ? '送出中...' : !leaveTableReady ? '⚠️ 功能未啟用' : `📋 ${_('attSubmitLeave')}`}
            </button>
          </div>

          {/* Leave request list */}
          {canManage && (
            <div style={{ display:'flex', gap:8, marginBottom:12 }}>
              {[['mine', _('attMyApplications')],['all', _('attAllApplications')]].map(([v,l]) => (
                <button key={v} style={{ ...st.btn(leaveTab===v?'primary':'default'), padding:'8px 16px' }} onClick={() => setLeaveTab(v)}>{l}</button>
              ))}
            </div>
          )}

          {/* Pending banner for admin */}
          {canManage && leaveTab === 'all' && leaveRequests.filter(l=>l.status==='pending').length > 0 && (
            <div style={{ background:C.yellowBg, border:`2px solid ${C.yellowBorder}`, borderRadius:12, padding:'12px 16px', marginBottom:14 }}>
              <div style={{ fontSize:14, fontWeight:800, color:C.yellow }}>⏳ 有 {leaveRequests.filter(l=>l.status==='pending').length} 筆請假申請待審核</div>
            </div>
          )}

          {(leaveTab === 'mine' ? leaveRequests.filter(l=>l.user_id===userProfile?.id) : leaveRequests)
            .map(l => {
              const ls = LEAVE_STATUS[l.status] || LEAVE_STATUS.pending
              const lt = LEAVE_TYPES.find(t=>t.value===l.leave_type)
              return (
                <div key={l.id} style={{ ...st.card, marginBottom:10, border:`1.5px solid ${ls.color}33` }}>
                  <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:8 }}>
                    <div>
                      {leaveTab==='all' && <div style={{ fontSize:13, fontWeight:700, color:C.accent, marginBottom:3 }}>{l.user_name}</div>}
                      <div style={{ fontSize:15, fontWeight:800, color:C.text }}>
                        {lt?.label || l.leave_type} · {l.start_date}{l.start_date!==l.end_date?` ~ ${l.end_date}`:''} ({l.days}天)
                      </div>
                      <div style={{ fontSize:13, color:C.muted, marginTop:4 }}>{l.reason}</div>
                      {l.reviewer_name && <div style={{ fontSize:11, color:C.muted, marginTop:4 }}>審核人：{l.reviewer_name}{l.reviewer_note?` · ${l.reviewer_note}`:''}</div>}
                      <div style={{ fontSize:11, color:C.muted, marginTop:2 }}>申請時間：{formatDate(l.created_at)}</div>
                    </div>
                    <div style={{ display:'flex', flexDirection:'column', gap:6, alignItems:'flex-end' }}>
                      <span style={{ padding:'5px 14px', borderRadius:12, fontSize:13, fontWeight:700, background:`${ls.color}22`, color:ls.color }}>{ls.label}</span>
                      {canManage && leaveTab==='all' && (
                        <div style={{ display:'flex', gap:6, flexWrap:'wrap', justifyContent:'flex-end' }}>
                          {l.status==='pending' && <>
                            <button style={{ ...st.btn('success'), padding:'6px 12px', fontSize:12, minHeight:34 }} onClick={() => handleReviewLeave(l.id,'approved')}>✓ {_('attApprove')}</button>
                            <button style={{ ...st.btn('danger'), padding:'6px 12px', fontSize:12, minHeight:34 }} onClick={() => { setRejectModal({ id:l.id, userName:l.user_name }); setRejectNote('') }}>✕ {_('attReject')}</button>
                          </>}
                          <button style={{ ...st.btn('warning'), padding:'6px 10px', fontSize:12, minHeight:34 }} onClick={() => { setEditLeave(l); setEditLeaveForm({ leave_type:l.leave_type, start_date:l.start_date, end_date:l.end_date, reason:l.reason||'', status:l.status }) }}>✏️ 修改</button>
                          <button style={{ padding:'6px 10px', background:'#fef2f2', border:`1.5px solid #fca5a5`, borderRadius:8, fontSize:12, color:C.red, cursor:'pointer', fontWeight:700, minHeight:34 }} onClick={() => handleDeleteLeave(l.id, l.user_name)}>🗑 刪除</button>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )
            })
          }
          {(leaveTab==='mine'?leaveRequests.filter(l=>l.user_id===userProfile?.id):leaveRequests).length===0 && (
            <EmptyState icon="📋" text={_('attNoLeaveRecords')} />
          )}
        </>
      )}

      {/* ───────── 出勤管理 (Admin) ───────── */}
      {activeTab === 'admin' && canManage && (
        <>
          {/* Stats row */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(2,1fr)', gap:12, marginBottom:20 }}>
            {[
              { icon:'👥', label:_('attTodayClockedIn'), value:`${stats.todayRecs.length} 人`, color:C.accent, bg:C.accentDim },
              { icon:'⚠️', label:_('attTodayLate'), value:`${stats.todayRecs.filter(r=>r.status==='late').length} 人`, color:C.yellow, bg:C.yellowBg },
              { icon:'❌', label:_('attTodayNotClockedIn'), value:`${stats.notClockedIn.length} 人`, color:C.red, bg:C.redBg },
              { icon:'📅', label:'本月出勤', value:`${stats.monthRecs.length} 筆`, color:'#7c3aed', bg:'#ede9fe' },
            ].map((k,i) => (
              <div key={i} style={{ background:k.bg, border:`2px solid ${k.color}33`, borderRadius:14, padding:'14px 16px' }}>
                <div style={{ fontSize:22, marginBottom:4 }}>{k.icon}</div>
                <div style={{ fontSize:24, fontWeight:900, color:k.color }}>{k.value}</div>
                <div style={{ fontSize:12, color:C.sub, fontWeight:600, marginTop:3 }}>{k.label}</div>
              </div>
            ))}
          </div>

          {/* Not clocked in today */}
          {stats.notClockedIn.length > 0 && (
            <div style={{ ...st.card, marginBottom:16, border:`2px solid ${C.redBorder}` }}>
              <div style={{ fontSize:14, fontWeight:800, color:C.red, marginBottom:10 }}>❌ 今日尚未打卡 ({stats.notClockedIn.length} 人)</div>
              <div style={{ display:'flex', flexWrap:'wrap', gap:8 }}>
                {stats.notClockedIn.map(u => (
                  <span key={u.id} style={{ padding:'5px 12px', background:C.redBg, border:`1.5px solid ${C.redBorder}`, borderRadius:20, fontSize:12, fontWeight:700, color:C.red }}>
                    {u.name}{u.department ? ` · ${u.department}` : ''}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Today summary cards */}
          {stats.todayRecs.length > 0 && (
            <div style={{ ...st.card, marginBottom:16 }}>
              <div style={{ fontSize:14, fontWeight:700, color:C.text, marginBottom:12 }}>📋 今日出勤摘要</div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(150px,1fr))', gap:8 }}>
                {users.map(u => {
                  const rec = stats.todayRecs.find(r => r.user_id === u.id)
                  const si = rec ? STATUS_INFO[rec.status]||STATUS_INFO.normal : null
                  return (
                    <div key={u.id} style={{ padding:'10px 12px', borderRadius:10, background: rec ? si.bg : '#f1f5f9', border:`1.5px solid ${rec ? si.border : C.border}` }}>
                      <div style={{ fontSize:13, fontWeight:700, color:C.text }}>{u.name}</div>
                      {rec ? (
                        <>
                          <div style={{ fontSize:11, color:C.muted, marginTop:2 }}>{formatTime(rec.clock_in)} {rec.clock_out ? `~ ${formatTime(rec.clock_out)}` : '(未下班)'}</div>
                          <div style={{ fontSize:11, fontWeight:700, color:si.color, marginTop:2 }}>{si.label}{rec.work_hours>0 ? ` · ${rec.work_hours}H` : ''}</div>
                        </>
                      ) : (
                        <div style={{ fontSize:11, color:C.red, fontWeight:700, marginTop:2 }}>尚未打卡</div>
                      )}
                    </div>
                  )
                })}
              </div>
            </div>
          )}

          {/* Pending Leave Approvals — shown prominently in admin tab */}
          {leaveTableReady && leaveRequests.filter(l => l.status === 'pending').length > 0 && (
            <div style={{ ...st.card, marginBottom:16, border:`2px solid ${C.yellowBorder}` }}>
              <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:12 }}>
                <div style={{ fontSize:14, fontWeight:800, color:C.yellow }}>
                  ⏳ 待審核請假申請（{leaveRequests.filter(l => l.status === 'pending').length} 筆）
                </div>
                <button style={{ ...st.btn('default'), padding:'6px 12px', fontSize:11 }} onClick={() => { setActiveTab('leave'); setLeaveTab('all') }}>查看全部 →</button>
              </div>
              {leaveRequests.filter(l => l.status === 'pending').map(l => {
                const lt = LEAVE_TYPES.find(t => t.value === l.leave_type)
                return (
                  <div key={l.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', padding:'12px 0', borderBottom:`1px solid ${C.border}`, flexWrap:'wrap', gap:10 }}>
                    <div>
                      <div style={{ fontSize:14, fontWeight:700, color:C.text }}>{l.user_name}</div>
                      <div style={{ fontSize:13, color:C.muted, marginTop:2 }}>
                        {lt?.label || l.leave_type} · {l.start_date}{l.start_date !== l.end_date ? ` ~ ${l.end_date}` : ''} &nbsp;
                        <span style={{ fontWeight:700, color:C.accent }}>({l.days} 天)</span>
                      </div>
                      {l.reason && <div style={{ fontSize:12, color:C.muted, marginTop:2 }}>原因：{l.reason}</div>}
                      <div style={{ fontSize:11, color:C.muted, marginTop:2 }}>申請時間：{formatDate(l.created_at)}</div>
                    </div>
                    <div style={{ display:'flex', gap:6, flexWrap:'wrap' }}>
                      <button style={{ ...st.btn('success'), padding:'7px 14px', fontSize:12 }} onClick={() => handleReviewLeave(l.id, 'approved')}>✓ {_('attApprove')}</button>
                      <button style={{ ...st.btn('danger'), padding:'7px 14px', fontSize:12 }} onClick={() => { setRejectModal({ id:l.id, userName:l.user_name }); setRejectNote('') }}>✕ {_('attReject')}（附理由）</button>
                      <button style={{ ...st.btn('warning'), padding:'7px 12px', fontSize:12 }} onClick={() => { setEditLeave(l); setEditLeaveForm({ leave_type:l.leave_type, start_date:l.start_date, end_date:l.end_date, reason:l.reason||'', status:l.status }) }}>✏️ 修改</button>
                      <button style={{ padding:'7px 12px', background:'#f1f5f9', border:`1.5px solid ${C.border}`, borderRadius:8, fontSize:12, color:C.red, cursor:'pointer', fontWeight:700 }} onClick={() => handleDeleteLeave(l.id, l.user_name)}>🗑 刪除</button>
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          {/* Toolbar */}
          <div style={{ display:'flex', gap:8, marginBottom:12, justifyContent:'space-between', flexWrap:'wrap' }}>
            <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
              <select style={{ ...st.input, width:140 }} value={filterUser} onChange={e => { setFilterUser(e.target.value); setPage(1) }}>
                <option value="all">全部員工</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
              <select style={{ ...st.input, width:120 }} value={filterStatus} onChange={e => { setFilterStatus(e.target.value); setPage(1) }}>
                <option value="all">全部狀態</option>
                {Object.entries(STATUS_INFO).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
              <input type="date" style={{ ...st.input, width:150 }} value={filterDateFrom} onChange={e => { setFilterDateFrom(e.target.value); setPage(1) }} placeholder="開始日期" />
              <span style={{ display:'flex', alignItems:'center', fontSize:13, color:C.muted }}>~</span>
              <input type="date" style={{ ...st.input, width:150 }} value={filterDateTo} onChange={e => { setFilterDateTo(e.target.value); setPage(1) }} />
              {(filterUser!=='all'||filterStatus!=='all'||filterDateFrom||filterDateTo) && (
                <button style={{ ...st.btn('default'), padding:'8px 12px', fontSize:12 }} onClick={() => { setFilterUser('all'); setFilterStatus('all'); setFilterDateFrom(''); setFilterDateTo(''); setPage(1) }}>✕ 清除</button>
              )}
            </div>
            <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
              <button style={{ ...st.btn('default'), padding:'10px 14px' }} onClick={handleExportCSV}>📤 {_('attExportCsv')}</button>
              <button style={{ ...st.btn('default'), padding:'10px 14px' }} onClick={() => setShowSettings(!showSettings)}>⚙️ 設定</button>
              {isAdmin && <button style={{ ...st.btn(), padding:'10px 14px' }} onClick={() => setShowManualAdd(true)}>＋ {_('attManualClock')}</button>}
            </div>
          </div>

          <div style={{ fontSize:12, color:C.muted, marginBottom:12 }}>共 {filteredRecords.length} 筆紀錄</div>

          {/* Settings panel */}
          {showSettings && (
            <div style={{ ...st.card, marginBottom:16, border:`2px solid ${C.accent}` }}>
              <div style={{ fontSize:15, fontWeight:700, color:C.text, marginBottom:14 }}>⚙️ 出勤設定</div>
              <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(170px,1fr))', gap:14, marginBottom:14 }}>
                <div><label style={st.label}>{_('attWorkStart')}</label><input type="time" style={st.input} value={settings.work_start} onChange={e => setSettings(s=>({...s,work_start:e.target.value}))} /></div>
                <div><label style={st.label}>{_('attWorkEnd')}</label><input type="time" style={st.input} value={settings.work_end} onChange={e => setSettings(s=>({...s,work_end:e.target.value}))} /></div>
                <div><label style={st.label}>{_('attLateThreshold')}</label><input type="number" min="0" style={st.input} value={settings.late_threshold} onChange={e => setSettings(s=>({...s,late_threshold:parseInt(e.target.value)||0}))} /></div>
              </div>
              <div style={{ padding:'12px 16px', background:C.accentDim, borderRadius:10, marginBottom:14 }}>
                <div style={{ fontSize:13, fontWeight:700, color:C.accent, marginBottom:10 }}>📍 GPS 打卡設定</div>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(170px,1fr))', gap:12, marginBottom:10 }}>
                  <div><label style={st.label}>工廠緯度</label><input type="number" step="any" style={st.input} value={settings.latitude||''} placeholder="24.1477" onChange={e => setSettings(s=>({...s,latitude:parseFloat(e.target.value)||null}))} /></div>
                  <div><label style={st.label}>工廠經度</label><input type="number" step="any" style={st.input} value={settings.longitude||''} placeholder="120.6736" onChange={e => setSettings(s=>({...s,longitude:parseFloat(e.target.value)||null}))} /></div>
                  <div><label style={st.label}>允許範圍（m）</label><input type="number" min="10" style={st.input} value={settings.allowed_radius||100} onChange={e => setSettings(s=>({...s,allowed_radius:parseInt(e.target.value)||100}))} /></div>
                </div>
                <div style={{ display:'flex', gap:10, alignItems:'center', flexWrap:'wrap' }}>
                  <button style={{ ...st.btn('default'), padding:'8px 14px', fontSize:12 }} onClick={() => {
                    if (!navigator.geolocation) { showToast('不支援 GPS','error'); return }
                    navigator.geolocation.getCurrentPosition(p => {
                      setSettings(s=>({...s,latitude:p.coords.latitude,longitude:p.coords.longitude}))
                      showToast('✓ 已取得目前位置')
                    }, () => showToast('無法取得位置','error'))
                  }}>📍 使用目前位置</button>
                  <label style={{ display:'flex', alignItems:'center', gap:8, cursor:'pointer' }}>
                    <input type="checkbox" checked={settings.require_location} onChange={e => setSettings(s=>({...s,require_location:e.target.checked}))} style={{ width:16, height:16 }} />
                    <span style={{ fontSize:13, color:C.text }}>{_('attRequireGps')}</span>
                  </label>
                </div>
              </div>
              <button style={{ ...st.btn(), padding:'12px 24px' }} onClick={handleSaveSettings} disabled={saving}>{saving ? _('loading') : _('attConfirmSave')}</button>
            </div>
          )}

          {/* Records table */}
          <div style={st.card}>
            <ScrollTable>
              <thead>
                <tr>{[[_('attThDate')],[_('attThEmployee')],['工號'],[_('attThClockIn')],[_('attThClockOut')],['工時'],['GPS'],[_('attThStatus')],['操作']].map(([zh]) => (
                  <th key={zh} style={st.th}>{zh}</th>
                ))}</tr>
              </thead>
              <tbody>
                {paged.length === 0 ? (
                  <tr><td colSpan={9}><EmptyState icon="⏰" text={_('attNoRecords')} /></td></tr>
                ) : paged.map(r => {
                  const si = STATUS_INFO[r.status] || STATUS_INFO.normal
                  return (
                    <tr key={r.id}>
                      <td style={{ ...st.td, fontWeight:700, color:C.text }}>{r.date}</td>
                      <td style={{ ...st.td, fontWeight:600, color:C.text }}>{r.user_name}</td>
                      <td style={{ ...st.td, fontFamily:'monospace', color:C.accent, fontSize:12 }}>{r.employee_id||'-'}</td>
                      <td style={{ ...st.td, color:C.green, fontWeight:700 }}>{formatTime(r.clock_in)}</td>
                      <td style={{ ...st.td, color:r.clock_out?C.yellow:C.muted, fontWeight:r.clock_out?700:400 }}>{formatTime(r.clock_out)}</td>
                      <td style={st.td}>{r.work_hours>0?<span style={{ color:r.work_hours>8?'#7c3aed':C.text, fontWeight:700 }}>{r.work_hours}H{r.work_hours>8?'⚡':''}</span>:'-'}</td>
                      <td style={st.td}>
                        {r.location_verified ? <span style={{ fontSize:12,color:C.green,fontWeight:700 }}>✅ {r.distance_meters}m</span>
                          : r.latitude ? <span style={{ fontSize:12,color:C.red }}>❌ 超出</span>
                          : <span style={{ color:C.muted,fontSize:12 }}>-</span>}
                      </td>
                      <td style={st.td}>
                        <span style={{ padding:'4px 10px', borderRadius:10, fontSize:12, fontWeight:700, background:si.bg, color:si.color, border:`1.5px solid ${si.border}` }}>{si.label}</span>
                      </td>
                      <td style={st.td}>
                        <div style={{ display:'flex', gap:4 }}>
                          <button style={{ ...st.btn('warning'), padding:'4px 8px', fontSize:11, minHeight:32 }} onClick={() => { setEditRecord(r); setEditForm({ clock_in:formatTime(r.clock_in), clock_out:r.clock_out?formatTime(r.clock_out):'', status:r.status, note:r.note||'' }) }}>編輯</button>
                          <button style={{ ...st.btn('danger'), padding:'4px 8px', fontSize:11, minHeight:32 }} onClick={() => handleDeleteRecord(r.id)}>刪除</button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </ScrollTable>
            <Pagination page={page} total={totalPages} onChange={setPage} />
          </div>
        </>
      )}

      {/* ───────── 統計分析 (Admin) ───────── */}
      {activeTab === 'analytics' && canManage && (
        <>
          {/* KPI */}
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))', gap:12, marginBottom:20 }}>
            {[
              { label:'本月出勤總筆', value:stats.monthRecs.length, color:C.accent },
              { label:'本月遲到次數', value:stats.monthRecs.filter(r=>r.status==='late').length, color:C.yellow },
              { label:'本月總工時', value:`${Math.round(stats.monthRecs.reduce((a,r)=>a+(r.work_hours||0),0))}H`, color:'#7c3aed' },
              { label:'本月加班工時', value:`${Math.round(stats.monthRecs.reduce((a,r)=>a+Math.max(0,(r.work_hours||0)-8),0)*10)/10}H`, color:C.green },
            ].map((k,i) => (
              <div key={i} style={{ background:'#fff', border:`2px solid ${k.color}33`, borderRadius:14, padding:'14px 16px' }}>
                <div style={{ fontSize:22, fontWeight:900, color:k.color }}>{k.value}</div>
                <div style={{ fontSize:11, color:C.sub, fontWeight:600, marginTop:4 }}>{k.label}</div>
              </div>
            ))}
          </div>

          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(280px,1fr))', gap:16 }}>
            {/* Monthly trend */}
            <div style={st.card}>
              <div style={{ fontSize:14, fontWeight:700, color:C.text, marginBottom:14 }}>📈 {_('attTrend6Months')}</div>
              <ColBarChart data={analyticsData.months} color={C.accent} />
            </div>

            {/* Per-person this month */}
            <div style={st.card}>
              <div style={{ fontSize:14, fontWeight:700, color:C.text, marginBottom:14 }}>👥 {_('attPersonalStats')}</div>
              <div style={{ overflowX:'auto' }}>
                <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                  <thead>
                    <tr>{['員工','天數','工時','遲到','加班'].map(h => <th key={h} style={{ ...st.th, fontSize:11 }}>{h}</th>)}</tr>
                  </thead>
                  <tbody>
                    {analyticsData.sortedByHours.map((p,i) => (
                      <tr key={i}>
                        <td style={{ ...st.td, fontWeight:700, color:C.text }}>{p.name}</td>
                        <td style={st.td}>{p.days}天</td>
                        <td style={{ ...st.td, color:'#7c3aed', fontWeight:700 }}>{Math.round(p.hours*10)/10}H</td>
                        <td style={{ ...st.td, color:p.late>0?C.yellow:C.muted, fontWeight:p.late>0?700:400 }}>{p.late>0?`${p.late}次`:'—'}</td>
                        <td style={{ ...st.td, color:p.overtime>0?C.green:C.muted, fontWeight:p.overtime>0?700:400 }}>{p.overtime>0?`${Math.round(p.overtime*10)/10}H`:'—'}</td>
                      </tr>
                    ))}
                    {analyticsData.sortedByHours.length===0 && <tr><td colSpan={5} style={st.td}><EmptyState icon="📊" text="本月尚無資料" /></td></tr>}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Late ranking */}
            {analyticsData.sortedByLate.length > 0 && (
              <div style={st.card}>
                <div style={{ fontSize:14, fontWeight:700, color:C.text, marginBottom:14 }}>⚠️ {_('attLateRanking')}</div>
                {analyticsData.sortedByLate.slice(0,8).map((p,i) => (
                  <div key={i} style={{ display:'flex', alignItems:'center', gap:10, marginBottom:8 }}>
                    <div style={{ width:20, fontSize:12, color:C.muted, fontWeight:700, textAlign:'right' }}>{i+1}</div>
                    <div style={{ flex:1 }}>
                      <div style={{ display:'flex', justifyContent:'space-between', fontSize:13, marginBottom:3 }}>
                        <span style={{ fontWeight:600, color:C.text }}>{p.name}</span>
                        <span style={{ color:C.yellow, fontWeight:800 }}>{p.late}次</span>
                      </div>
                      <div style={{ height:6, background:C.border, borderRadius:3 }}>
                        <div style={{ height:'100%', width:`${(p.late/analyticsData.sortedByLate[0].late)*100}%`, background:`${C.yellow}cc`, borderRadius:3 }} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Export */}
            <div style={{ ...st.card, display:'flex', flexDirection:'column', gap:12 }}>
              <div style={{ fontSize:14, fontWeight:700, color:C.text }}>📤 {_('attExportReport')}</div>
              <div style={{ fontSize:12, color:C.muted }}>匯出當前篩選條件下的出勤紀錄 CSV</div>
              <button style={{ ...st.btn('success'), padding:'12px 20px' }} onClick={handleExportCSV}>📤 匯出出勤紀錄 CSV</button>
              <button style={{ ...st.btn('default'), padding:'12px 20px' }} onClick={() => {
                const rows = analyticsData.sortedByHours.map(p => [p.name, p.days, Math.round(p.hours*10)/10, p.late, p.early, Math.round(p.overtime*10)/10])
                const headers = ['員工','出勤天數','總工時(H)','遲到次數','早退次數','加班工時(H)']
                const csv = [headers,...rows].map(r=>r.map(v=>`"${v}"`).join(',')).join('\n')
                const blob = new Blob(['\uFEFF'+csv],{type:'text/csv;charset=utf-8;'})
                const url = URL.createObjectURL(blob); const a=document.createElement('a'); a.href=url
                a.download=`YPFOPS_個人出勤統計_${new Date().toISOString().slice(0,7)}.csv`; a.click()
              }}>📊 匯出個人統計 CSV</button>
            </div>
          </div>
        </>
      )}

      {/* ── Manual Add Modal ── */}
      {showManualAdd && (
        <Modal title="＋ 手動補打卡" onClose={() => setShowManualAdd(false)}>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(170px,1fr))', gap:14 }}>
            <div style={{ gridColumn:'span 2' }}>
              <label style={st.label}>員工 *</label>
              <select style={st.input} value={manualForm.user_id} onChange={e => setManualForm(f=>({...f,user_id:e.target.value}))}>
                <option value="">-- 選擇員工 --</option>
                {users.map(u => <option key={u.id} value={u.id}>{u.name}{u.employee_id?` (${u.employee_id})`:''}</option>)}
              </select>
            </div>
            <div>
              <label style={st.label}>日期 *</label>
              <input type="date" style={st.input} value={manualForm.date} max={todayStr} onChange={e => setManualForm(f=>({...f,date:e.target.value}))} />
            </div>
            <div>
              <label style={st.label}>上班狀態</label>
              <select style={st.input} value={manualForm.status} onChange={e => setManualForm(f=>({...f,status:e.target.value}))}>
                {Object.entries(STATUS_INFO).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div>
              <label style={st.label}>上班時間 *</label>
              <input type="time" style={st.input} value={manualForm.clock_in} onChange={e => setManualForm(f=>({...f,clock_in:e.target.value}))} />
            </div>
            <div>
              <label style={st.label}>下班時間（選填）</label>
              <input type="time" style={st.input} value={manualForm.clock_out} onChange={e => setManualForm(f=>({...f,clock_out:e.target.value}))} />
            </div>
            <div style={{ gridColumn:'span 2' }}>
              <label style={st.label}>備注</label>
              <input style={st.input} value={manualForm.note} placeholder="補打卡原因..." onChange={e => setManualForm(f=>({...f,note:e.target.value}))} />
            </div>
          </div>
          <div style={{ display:'flex', gap:10, marginTop:18 }}>
            <button style={{ ...st.btn(), flex:1, padding:'14px' }} onClick={handleManualAdd} disabled={saving}>{saving?'處理中...':'確認補打卡'}</button>
            <button style={{ ...st.btn('default'), padding:'14px 20px' }} onClick={() => setShowManualAdd(false)}>{_('cancel')}</button>
          </div>
        </Modal>
      )}

      {/* ── Reject Leave Modal (with note) ── */}
      {rejectModal && (
        <Modal title={`✕ 拒絕請假申請 — ${rejectModal.userName}`} onClose={() => setRejectModal(null)}>
          <div style={{ marginBottom:14 }}>
            <label style={st.label}>拒絕理由（選填）</label>
            <textarea style={{ ...st.input, height:80, resize:'vertical' }} value={rejectNote} placeholder="請填寫拒絕理由，將通知申請人..." onChange={e => setRejectNote(e.target.value)} />
          </div>
          <div style={{ display:'flex', gap:10 }}>
            <button style={{ ...st.btn('danger'), flex:1, padding:'14px' }} onClick={() => { handleReviewLeave(rejectModal.id, 'rejected', rejectNote); setRejectModal(null) }}>確認拒絕</button>
            <button style={{ ...st.btn('default'), padding:'14px 20px' }} onClick={() => setRejectModal(null)}>{_('cancel')}</button>
          </div>
        </Modal>
      )}

      {/* ── Edit Leave Modal ── */}
      {editLeave && (
        <Modal title={`✏️ 修改請假申請 — ${editLeave.user_name}`} onClose={() => setEditLeave(null)}>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(160px,1fr))', gap:14 }}>
            <div>
              <label style={st.label}>假別</label>
              <select style={st.input} value={editLeaveForm.leave_type} onChange={e => setEditLeaveForm(f=>({...f,leave_type:e.target.value}))}>
                {LEAVE_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label style={st.label}>開始日期</label>
              <input type="date" style={st.input} value={editLeaveForm.start_date} onChange={e => setEditLeaveForm(f=>({...f,start_date:e.target.value}))} />
            </div>
            <div>
              <label style={st.label}>結束日期</label>
              <input type="date" style={st.input} value={editLeaveForm.end_date} min={editLeaveForm.start_date} onChange={e => setEditLeaveForm(f=>({...f,end_date:e.target.value}))} />
            </div>
            <div>
              <label style={st.label}>狀態</label>
              <select style={st.input} value={editLeaveForm.status} onChange={e => setEditLeaveForm(f=>({...f,status:e.target.value}))}>
                <option value="pending">待審核</option>
                <option value="approved">已核准</option>
                <option value="rejected">已拒絕</option>
              </select>
            </div>
          </div>
          <div style={{ marginTop:14 }}>
            <label style={st.label}>請假原因</label>
            <textarea style={{ ...st.input, height:70, resize:'vertical' }} value={editLeaveForm.reason} onChange={e => setEditLeaveForm(f=>({...f,reason:e.target.value}))} />
          </div>
          {editLeaveForm.start_date && editLeaveForm.end_date && editLeaveForm.end_date >= editLeaveForm.start_date && (
            <div style={{ marginTop:10, padding:'10px 14px', background:C.accentDim, borderRadius:10, fontSize:13, color:C.accent, fontWeight:700 }}>
              共 {Math.round((new Date(editLeaveForm.end_date)-new Date(editLeaveForm.start_date))/86400000)+1} 天
            </div>
          )}
          <div style={{ display:'flex', gap:10, marginTop:18 }}>
            <button style={{ ...st.btn(), flex:1, padding:'14px' }} onClick={handleEditLeave} disabled={saving}>{saving?'儲存中...':'確認更新'}</button>
            <button style={{ ...st.btn('default'), padding:'14px 20px' }} onClick={() => setEditLeave(null)}>{_('cancel')}</button>
          </div>
        </Modal>
      )}

      {/* ── Edit Record Modal ── */}
      {editRecord && (
        <Modal title={`✏️ 編輯紀錄 — ${editRecord.user_name} · ${editRecord.date}`} onClose={() => setEditRecord(null)}>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
            <div>
              <label style={st.label}>上班時間</label>
              <input type="time" style={st.input} value={editForm.clock_in} onChange={e => setEditForm(f=>({...f,clock_in:e.target.value}))} />
            </div>
            <div>
              <label style={st.label}>下班時間</label>
              <input type="time" style={st.input} value={editForm.clock_out} onChange={e => setEditForm(f=>({...f,clock_out:e.target.value}))} />
            </div>
            <div>
              <label style={st.label}>狀態</label>
              <select style={st.input} value={editForm.status} onChange={e => setEditForm(f=>({...f,status:e.target.value}))}>
                {Object.entries(STATUS_INFO).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
            <div>
              <label style={st.label}>備注</label>
              <input style={st.input} value={editForm.note} onChange={e => setEditForm(f=>({...f,note:e.target.value}))} />
            </div>
          </div>
          <div style={{ display:'flex', gap:10, marginTop:18 }}>
            <button style={{ ...st.btn(), flex:1, padding:'14px' }} onClick={handleEditRecord} disabled={saving}>{saving?'儲存中...':'確認更新'}</button>
            <button style={{ ...st.btn('default'), padding:'14px 20px' }} onClick={() => setEditRecord(null)}>{_('cancel')}</button>
          </div>
        </Modal>
      )}
    </div>
  )
}
